<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        :class="['slice slice-features slice-features--side', {'slice-features--side--two-cols': slice.primary.nbColumn === '2'}]"
        aria-label="features"
    >
        <div class="slice-content">
            <div class="slice-features--side__header">
                <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" />
                <PrismicRichText v-if="slice.primary.description" :field="slice.primary.description" class="slice-description" />
                <div v-if="cta" class="slice-features--side__cta">
                    <VJoyButton :href="$withLocalePrefix(cta.url)" :variant="cta.variant" data-component-source="proof-points">
                        {{ cta.text }}
                    </VJoyButton>
                </div>
                <PrismicImage v-if="isFilled.image(slice.primary.image)" :field="slice.primary.image" class="slice-features--side__image" />
            </div>
            <div class="slice-features--side__content">
                <div v-for="(item, i) in slice.items" :key="i" class="slice-features--side__item">
                    <div class="slice-features--side__item-image">
                        <PrismicImage :field="item.image" />
                    </div>
                    <div>
                        <div class="slice-features--side__item-title">{{ item.title }}</div>
                        <PrismicRichText :field="item.text" class="slice-features--side__item-text" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {FeaturesSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';

    type FeaturesSideInZone = Extract<FeaturesSlice, {variation: 'featuresSide'}>;
    const props = defineProps(getSliceComponentProps<FeaturesSideInZone>());

    const section = useTemplateRef('section');

    type Cta = {url: string; text: string; variant: 'primary' | 'secondary' | undefined};

    const cta = computed<Cta | null>(() => {
        const link = props.slice.primary.cta;
        if (!isFilled.link(link)) {
            return null;
        }

        const filledLink = link as {url: string; text?: string; variant?: 'primary' | 'secondary'};
        return {
            url: filledLink.url,
            text: filledLink.text ?? '',
            variant: filledLink.variant ?? undefined,
        };
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-features--side {
        position: relative;

        &__cta {
            margin-top: var(--joy-core-spacing-6);
        }

        &__image {
            margin-top: var(--joy-core-spacing-4);
            max-width: 600px;
            height: auto;
        }

        @include mq.screen_md(min) {
            .slice-content {
                display: grid;
                grid-template-columns: 1fr;
                justify-items: center;
                gap: var(--joy-core-spacing-8);

                @include mq.screen_md(min) {
                    grid-template-columns: 1fr 1fr;
                    justify-content: center;
                    text-align: left;
                }
            }
        }

        &__item {
            display: flex;
            text-align: left;
            gap: var(--joy-core-spacing-5);
            margin-bottom: var(--joy-core-spacing-6);

            &:first-child {
                margin-top: var(--joy-core-spacing-8);
            }

            @include mq.screen_md(min) {
                &:first-child {
                    margin-top: 0;
                }
            }

            &-image {
                flex: none;
                width: 40px;

                > img {
                    width: 100%;
                }
            }

            &-title {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
                margin-bottom: var(--joy-core-spacing-2);

                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-primary-800);
                }
            }

            &-text {
                li {
                    list-style-type: none;
                    padding-left: var(--joy-core-spacing-6);
                    margin-bottom: var(--joy-core-spacing-3);
                    background: url('../../../../assets/svg/check.svg') no-repeat;
                    background-position: left 3px;
                }
            }
        }

        &--two-cols {
            .slice-features--side__content {
                display: flex;
                align-items: flex-start;
                flex-wrap: wrap;
                gap: 0 var(--joy-core-spacing-8);
            }

            .slice-features--side__item {
                @include mq.screen_md(min) {
                    flex: none;
                    width: calc(50% - var(--joy-core-spacing-4));
                }
            }
        }
    }
</style>
